import React from 'react';
import { ICustomHeader, gridControlType } from 'app/models/Grid';
import NoteIcon from 'app/assets/images/Notes.png';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CredentialingLookup } from 'app/models/Credentialing/CredentialingLookups';
import {
  getDate,
  getDateNoFormat,
  getDateText,
  getCredentialName,
  getCredentialNameNoFormat,
  detailColumnsAndRows,
  getMetadataForReqID,
  getCredentialNameBold,
} from '../tableHelpers';
import { ICredentials } from 'app/models/Credentialing/Credentials';
import HistoryIcon from '@mui/icons-material/History';
import { nestedChildren } from 'app/helpers/arrayHelpers';
import VersionDetailsHoverView from '../VersionDetailsHoverView';
import { AttachmentAction, CredentialAttachmentToolTip } from '../../Attachments/AttachmentHoverView';
import { groupedEmployeeHealths } from './EmployeeHealth';
import { childSorting } from '../HumanResources/HumanResourcesGroupingHelper';

const getMainTableColumns = (
  viewingDuplicates: boolean = false,
  historical: boolean = false,
  sortable: boolean = true,
) => {
  const headers: ICustomHeader[] = [
    {
      title: 'credentialName',
      value: 'Credential Name',
      sortable: sortable,
      sortByField: 'credentialNameNoFormat',
    },
    {
      title: 'dateCompleted',
      value: 'Date Completed',
      sortable: sortable,
      defaultSort: !viewingDuplicates,
      sortByField: 'dateCompletedNoFormat',
      control: {
        type: gridControlType.date,
      },
    },
    {
      title: 'lastUpdate',
      value: 'Updated Date',
      sortByField: 'lastUpdateNoFormat',
      sortable: sortable,
      defaultSort: viewingDuplicates,
      control: {
        type: gridControlType.label,
      },
    },
    {
      title: 'versioning',
      value: 'Versions',
      icon: true,
      versioning: !historical,
    },
    {
      title: 'documentIcon',
      value: 'Document',
      // clickable: true,
      navigationIds: [''],
      navigationExp: '/',
      hasHoverView: true,
      customHoverView: true,
    },
  ];
  return headers;
};

const flattenData = (
  employeeHealths: ICredentials[],
  lookups: CredentialingLookup[],
  viewingDuplicates = false,
  onAttachmentAction: (
    action: AttachmentAction,
    reqID: number,
    requirementTypeID: number,
    travelerId: number,
    brandId: number,
  ) => void,
  level: number = 0,
  expandable: boolean = true,
) => {
  let nestedItems = employeeHealths;
  if (level === 0) {
    const grouped = groupedEmployeeHealths(employeeHealths);
    nestedItems = nestedChildren(grouped);
  }
  if (level > 0) {
    nestedItems = childSorting(nestedItems, lookups);
  }
  const credentials: any[] = [];
  nestedItems?.forEach((item: ICredentials) => {
    const fieldLookups = getMetadataForReqID(lookups, item);

    const l: any = {
      rowID: item.requirementID,
      credentialName:
        level === 0 ? (
          getCredentialName(item.requirementType, item.credentialName)
        ) : (
          <VersionDetailsHoverView
            credential={[...detailColumnsAndRows(item, fieldLookups, ['DateCompleted', 'LastUpdate'], true)]}
            fieldLookups={fieldLookups}
            title={getCredentialName(item.requirementType, item.credentialName)}
            titleBold={getCredentialNameBold(item.requirementType, item.credentialName)}
          />
        ),
      credentialNameNoFormat: getCredentialNameNoFormat(item.requirementType, item.credentialName),
      dateCompleted: getDate(fieldLookups, 'DateCompleted', item.dateCompleted),
      dateCompletedNoFormat: getDateNoFormat(fieldLookups, 'DateCompleted', item.dateCompleted),
      lastUpdate: getDateText(fieldLookups, 'LastUpdate', item.lastUpdate),
      lastUpdateNoFormat: getDateNoFormat(fieldLookups, 'LastUpdate', item.lastUpdate),
      versioning: level === 0 ? <HistoryIcon /> : null,
      documentIcon: NoteIcon,
      moreIcon: <MoreVertIcon />,
      expandable: expandable,
      detailRows: [[...detailColumnsAndRows(item, fieldLookups, ['DateCompleted', 'LastUpdate'])]],
      documentIconHoverViewComponent: (
        <CredentialAttachmentToolTip
          onSelect={onAttachmentAction}
          requirementID={item.requirementID}
          requirementTypeID={item.requirementTypeID}
          travelerId={0}
          brandId={0}
        />
      ),
      versionRows:
        level === 0 && item['children'] !== undefined
          ? flattenData(item['children'], lookups, false, onAttachmentAction, level + 1, false)
          : null,
      versionColumns: getMainTableColumns(true, false, true),
    };
    credentials.push(l);
  });
  if (level === 0) {
    return credentials.sort(
      viewingDuplicates
        ? (a: any, b: any) => (a.lastUpdateNoFormat < b.lastUpdateNoFormat ? 1 : -1)
        : (a: any, b: any) => (a.expirationDateNoFormat > b.expirationDateNoFormat ? 1 : -1),
    );
  } else return credentials;
};

export const employeeHealthMedicalRecordsData = {
  getTableColumns: getMainTableColumns,
  getTableRows: flattenData,
};
